import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CompetitionDTO } from '../../services/apiClient';
import { useCompetitionApi } from '../../hooks/useCompetitionApi';

type CompetitionState =
    | 'pre-registration'
    | 'registration-open'
    | 'registration-and-competition-open'
    | 'registration-closed-pre-start'
    | 'competition-running';

type Slide = {
    lines: string[];
    targetMs: number;
};

const getCompetitionState = (competition: CompetitionDTO, now: Date): CompetitionState | null => {
    const regOpen = new Date(competition.registrationOpenDate ?? '');
    const regClose = new Date(competition.registrationCloseDate ?? '');
    const start = new Date(competition.startDate ?? '');
    const end = new Date(competition.endDate ?? '');

    if (now > end) return null;
    if (now < regOpen) return 'pre-registration';
    if (now >= regOpen && now <= regClose && now < start) return 'registration-open';
    if (now >= regOpen && now <= regClose && now >= start) return 'registration-and-competition-open';
    if (now > regClose && now < start) return 'registration-closed-pre-start';
    if (now >= start && now <= end) return 'competition-running';

    return null;
};

const getMainSlide = (state: CompetitionState, competition: CompetitionDTO): Slide => {
    switch (state) {
        case 'pre-registration':
            return { lines: ['Entries open in'], targetMs: new Date(competition.registrationOpenDate ?? '').getTime() };
        case 'registration-open':
            return { lines: ['Entries open', 'Competition starts in'], targetMs: new Date(competition.startDate ?? '').getTime() };
        case 'registration-and-competition-open':
            return { lines: ['Entries open', 'Competition open', 'Closes in'], targetMs: new Date(competition.endDate ?? '').getTime() };
        case 'registration-closed-pre-start':
            return { lines: ['Entries closed', 'Competition starts in'], targetMs: new Date(competition.startDate ?? '').getTime() };
        case 'competition-running':
            return { lines: ['Entries closed', 'Competition open', 'Closes in'], targetMs: new Date(competition.endDate ?? '').getTime() };
    }
};

const buildSlides = (state: CompetitionState, competition: CompetitionDTO, now: Date): Slide[] => {
    const mainSlide = getMainSlide(state, competition);
    const targetOpenMs = new Date(competition.targetDownloadOpenDate ?? '').getTime();
    const targetCloseMs = new Date(competition.targetDownloadCloseDate ?? '').getTime();
    const nowMs = now.getTime();
    const targetsAvailable = nowMs >= targetOpenMs && nowMs <= targetCloseMs;
    const targetsPending = nowMs < targetOpenMs;

    if (targetsAvailable) {
        return [
            { lines: ['Targets available', 'Download closes in'], targetMs: targetCloseMs },
            mainSlide,
        ];
    }

    if (targetsPending) {
        return [
            mainSlide,
            { lines: ['Targets available in'], targetMs: targetOpenMs },
        ];
    }

    return [mainSlide];
};

const useCountdown = (targetMs: number) => {
    const [timeLeft, setTimeLeft] = useState(() => Math.max(0, targetMs - Date.now()));

    useEffect(() => {
        const tick = () => setTimeLeft(Math.max(0, targetMs - Date.now()));
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [targetMs]);

    const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

    return { days, hours, minutes, seconds };
};

const CountdownUnit = ({ value, label }: { value: number; label: string }) => (
    <div className="flex flex-col items-center min-w-[36px]">
        <span className="text-xl font-bold tabular-nums text-white leading-none">
            {String(value).padStart(2, '0')}
        </span>
        <span className="text-[9px] uppercase tracking-widest text-white/60 mt-0.5">{label}</span>
    </div>
);

const SLIDE_DURATION_MS = 5000;
const FADE_DURATION_MS = 600;

const CompetitionCard = ({ competition }: { competition: CompetitionDTO }) => {
    const navigate = useNavigate();
    const [now, setNow] = useState(new Date());
    const [activeIndex, setActiveIndex] = useState(0);
    const [visible, setVisible] = useState(true);
    const swapTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(id);
    }, []);

    const state = getCompetitionState(competition, now);
    const slides = state ? buildSlides(state, competition, now) : [];
    const clampedIndex = Math.min(activeIndex, Math.max(slides.length - 1, 0));
    const activeSlide = slides[clampedIndex];

    // Reset to first slide when the number of slides changes (e.g. targets expire)
    useEffect(() => {
        setActiveIndex(0);
        setVisible(true);
    }, [slides.length]);

    // Cycle between slides when more than one is available
    useEffect(() => {
        if (slides.length <= 1) return;

        const id = setInterval(() => {
            setVisible(false);
            swapTimeoutRef.current = setTimeout(() => {
                setActiveIndex(prev => (prev + 1) % slides.length);
                setVisible(true);
            }, FADE_DURATION_MS);
        }, SLIDE_DURATION_MS);

        return () => {
            clearInterval(id);
            if (swapTimeoutRef.current) clearTimeout(swapTimeoutRef.current);
        };
    }, [slides.length]);

    const countdownTargetMs = activeSlide?.targetMs ?? new Date(competition.endDate ?? '').getTime() ?? Date.now();
    const { days, hours, minutes, seconds } = useCountdown(countdownTargetMs);

    if (!state || !activeSlide) return null;

    return (
        <button
            onClick={() => navigate('/admin/competitionentries/create')}
            className="w-[335px] text-left rounded-2xl shadow hover:shadow-md transition-shadow duration-200 overflow-hidden relative group"
        >
            <img
                src="/images/Competition_Banner.png"
                alt=""
                className="w-[335px] object-cover"
            />
            <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-colors duration-200" />

            <div
                className="absolute inset-0 p-4 flex items-end justify-between"
                style={{ opacity: visible ? 1 : 0, transition: `opacity ${FADE_DURATION_MS}ms ease-in-out` }}
            >
                <div className="flex flex-col gap-0.5">
                    {activeSlide.lines.map((line, i) => (
                        <span key={i} className="text-sm text-white font-medium leading-snug pl-1.5">
                            {line}
                        </span>
                    ))}
                    <div className="flex items-end gap-1 mt-1">
                        <CountdownUnit value={days} label="days" />
                        <span className="text-white/40 font-bold pb-3 text-sm">:</span>
                        <CountdownUnit value={hours} label="hrs" />
                        <span className="text-white/40 font-bold pb-3 text-sm">:</span>
                        <CountdownUnit value={minutes} label="min" />
                        <span className="text-white/40 font-bold pb-3 text-sm">:</span>
                        <CountdownUnit value={seconds} label="sec" />
                    </div>
                </div>

                {slides.length > 1 && (
                    <div className="flex flex-col gap-1 self-end pb-1">
                        {slides.map((_, i) => (
                            <div
                                key={i}
                                className={`w-1 h-1 rounded-full transition-colors duration-300 ${i === clampedIndex ? 'bg-white' : 'bg-white/30'}`}
                            />
                        ))}
                    </div>
                )}
            </div>
        </button>
    );
};

const CompetitionBanners: React.FC = () => {
    const { search } = useCompetitionApi();
    const [competitions, setCompetitions] = useState<CompetitionDTO[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchCompetitions = async () => {
            try {
                const response = await search({
                    filters: {},
                    page: 1,
                    pageSize: 9999,
                    sortBy: 'StartDate',
                    sortDescending: false,
                });
                const all: CompetitionDTO[] = response?.data?.results ?? [];
                const now = new Date();
                const visible = all.filter((c) => {
                    const showFrom = new Date(c.showBannerDate ?? '');
                    const end = new Date(c.endDate ?? '');
                    return showFrom <= now && end >= now;
                });
                setCompetitions(visible);
            } catch (error) {
                console.error('Error fetching competitions:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchCompetitions();
    }, []);

    if (loading || competitions.length === 0) return null;

    const currentYear = new Date().getFullYear();

    return (
        <div className="mb-8">
            <h2 className="text-sm font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-4">
                NHSA {currentYear} National Postal Competition
            </h2>
            <div className="flex flex-col gap-3 max-w-sm">
                {competitions.map((competition) => (
                    <CompetitionCard key={competition.id} competition={competition} />
                ))}
            </div>
        </div>
    );
};

export default CompetitionBanners;
